import { useEffect, useState } from 'react';
import { CustomGroupRenderer, MultiSelectDropdown, MultiSelectGroup } from '../MultiselectDropdown';
import { TextBlock } from '../../Atoms/Text';
import { Checkbox, MenuItem, Typography } from '@mui/material';
import { MultiSelectRoleExpand } from './MultiSelectRoleExpand';
import { getUser } from '../../../store/User';
import { getFeatureFlagSubject, defaultFeatureFlags, FeatureFlags } from '../../../Api/useFeatureFlags';
import { useWindowDimensions } from '../../../utils/WindowDimensions';
import { ADVISE_ROLE_GROUP } from './adviseRoleGroup';
import { roleGroups } from './roleGroups';
import { grantableRoleGroups, roleName, roleTitle } from './grantableRoles';

export interface RoleSelectionFormProps {
  selectedRoles: string[];
  updateRoles: (domains: string[]) => void;
  drawer?: boolean;
  size?: 'small' | 'medium' | undefined;
  styles?: string;
  hasError?: boolean;
  errorText?: string;
}

export const RoleSelectionForm = ({
  selectedRoles,
  updateRoles,
  drawer = false,
  size,
  styles,
  hasError = false,
  errorText
}: RoleSelectionFormProps) => {
  const [selectedRoleLabels, setSelectedRoleLabels] = useState<string[]>(selectedRoles);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(defaultFeatureFlags);
  const { width } = useWindowDimensions();

  useEffect(() => {
    const sub = getFeatureFlagSubject().subscribe(setFeatureFlags);
    return () => sub.unsubscribe();
  }, []);

  useEffect(() => {
    setSelectedRoleLabels(selectedRoles);
  }, [JSON.stringify(selectedRoles)]);

  const onHandleOptionClick = (option: string) => {
    const existingRoles = [...selectedRoles];
    if (existingRoles.some((role) => role === option)) {
      updateRoles(existingRoles.filter((role) => role !== option));
    } else {
      updateRoles([...existingRoles, option]);
    }
  };

  const handleIsOptionDisabled = (title: string, label: string, selectedItems: string[]): boolean => {
    const allGroups = featureFlags.advise ? [...roleGroups, ADVISE_ROLE_GROUP] : roleGroups;
    const roleGroup = allGroups.find((group) => group.title === title);
    const role = roleGroup?.items.find((item) => item.label === label);
    return role?.mutualExclusiveWith?.some((mew) => selectedItems.includes(mew)) || false;
  };

  const renderRoleOptions = (group: MultiSelectGroup, selectedItems: string[], clickHandler: CustomGroupRenderer) => {
    return group.items.map((item) => (
      <MenuItem
        key={`${group.title}-${item.label}`}
        onClick={(e) => {
          e.stopPropagation();
          clickHandler(group.title, item.label);
        }}
        value={roleName(group.title, item.label)}
        data-testid={group.title}
        data-cy={`${group.title.toLowerCase()}-${item.label.toLowerCase().replace(' ', '-')}`}
        disabled={handleIsOptionDisabled(group.title, item.label, selectedItems)}>
        <Checkbox checked={selectedItems?.includes(roleName(group.title, item.label))} />
        <Typography>{item.label}</Typography>
      </MenuItem>
    ));
  };

  const menuPropsOverride =
    width < 1023 ? { width: '13rem', minWidth: 'unset', maxHeight: 448 } : { width: 333, maxHeight: 448 };

  return (
    <div className={styles}>
      {!drawer && <TextBlock className="my-[4%] px-[4%] text-md">Select the roles for this user.</TextBlock>}
      <MultiSelectDropdown
        data-testid="role-select"
        dataCy="role-dropdown"
        groups={grantableRoleGroups(getUser(), featureFlags.advise)}
        selectedItems={selectedRoleLabels}
        onHandleOptionClick={onHandleOptionClick}
        placeholderText="Select roles..."
        customTitleHandler={roleTitle}
        customLabelHandler={roleName}
        groupDetailComponent={MultiSelectRoleExpand}
        customGroupRenderer={renderRoleOptions}
        size={size}
        menuPropsOverride={menuPropsOverride}
        hasError={hasError}
        errorText={errorText}
      />
    </div>
  );
};
